<template>
    <page :tabs="tabs">
        <grid
            :columnDefs="columnDefs"
            :fetchCallback="fetchCallback"
            ref="grid"
            :buttonToolbarClass="['align-items-xl-start', 'ml-1', 'mt-1']"
            row-selection="multiple"
            :suppress-row-click-selection="true"
            @selection-changed="handleSelectionChange"
            :isSearch="true"
            :isAutoWidth="true"
            :defaultSort="sort"
            :maxBlocksInCache="null"
        >
            <div class="d-flex flex-xs-row flex-column">
                <bulk-actions
                    class="mb-0"
                    v-if="hasSelectedRows"
                    :selected-count="currentSelectedRows.length"
                    :bulk-action-defs="bulkActionDefs"
                    :label="selectedActionLabel"
                >
                </bulk-actions>
                <div
                    class="
                        amount-wrapper
                        mr-2
                        mt-2 mt-xs-0
                        d-flex
                        align-items-center
                    "
                    v-if="hasSelectedRows"
                >
                    <b>Amount: {{ paymentAmount }}</b>
                </div>
            </div>

            <form-input
                v-model="paymentId"
                placeholder="Pmt Id"
                v-if="hasSelectedRows && isChangePmtIdSelected"
                class="mr-1"
            />
            <responsive-dropdown
                v-if="hasSelectedRows && isChangeStatusSelected"
                :items="changeStatusOptions"
                variant="primary"
                >{{ changeStatusLabel }}
            </responsive-dropdown>
            <b-button
                v-if="hasSelectedRows && (isChangePmtIdSelected || isChangeStatusSelected)"
                @click="applyChanges"
                variant="primary"
                >Go</b-button
            >
            <date-range-picker
                v-if="!hasSelectedRows"
                :range-options="predefinedRanges"
                :value="dateRange"
                :date-format="userUnicodeDateFormat"
                @input="onDaterangeChange"
            ></date-range-picker>
            <responsive-dropdown
                v-if="!hasSelectedRows"
                :items="statusOptions"
                variant="primary"
                class="mr-1 d-none d-xl-flex"
                >{{ selectedStatusLabel }}
            </responsive-dropdown>
            <export-to-excel-button v-if="!hasSelectedRows" :href="excelHref" />
            <form-search-field
                class="d-xl-flex d-none"
                v-if="!hasSelectedRows"
                :options="subscribersList"
                @input="searchSubscribers"
                :busy="busy"
                :value="subscriberSearch"
                @select="selectSubscriber"
                placeholder="Filter By Subscriber"
            />
            <b-button-group>
                <more-filters-dropdown
                    class="d-xl-none d-block"
                    v-if="!hasSelectedRows"
                    v-slot:default="{ handleDropdownShow, handleDropdownHide }"
                >
                        <form-search-field
                            class="d-xl-none d-flex"
                            v-if="!hasSelectedRows"
                            :options="subscribersList"
                            @input="searchSubscribers"
                            :busy="busy"
                            :value="subscriberSearch"
                            @select="selectSubscriber"
                            placeholder="Filter By Subscriber"
                        />
                        <responsive-dropdown
                            v-if="!hasSelectedRows"
                            :items="statusOptions"
                            variant="primary"
                            class="
                                mb-0
                                mr-1
                                d-block d-xl-none
                                damage-claim-dropdown
                            "
                            @show="handleDropdownShow"
                            @hide="handleDropdownHide"
                            >{{ selectedStatusLabel }}
                        </responsive-dropdown>
                </more-filters-dropdown>
            </b-button-group>
        </grid>
    </page>
</template>

<script>
    import API from '../../../api';
    import { ADMIN_DAMAGE_CLAIMS_LIST_ENDPOINT } from '../../../endpoints';
    import debounce from 'lodash/debounce';
    import Grid from '../../generic/grid/grid.vue';
    import page from '../../generic/page/page.vue';
    import { mapGetters } from 'vuex';
    import format from 'date-fns/format';
    import parseISO from 'date-fns/parseISO';
    import FormInput from '../../generic/form-input/form-input.vue';
    import FormSelect from '../../generic/form-select/form-select.vue';
    import FormSearchField from '../../generic/form-search-field/form-search-field.vue';
    import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';

    import SearchMultiselect from '../../generic/search-multiselect/search-multiselect.vue';
    import ExportToExcelButton from '../../generic/export-to-excel-button/export-to-excel-button.vue';
    import MoreFiltersDropdown from '../../generic/more-filters-dropdown/more-filters-dropdown.vue';
    import DateRangePicker, {
        LAST_MONTH,
        LAST_WEEK,
        LAST_YEAR,
        nameToRange,
        THIS_MONTH,
        THIS_WEEK,
        THIS_YEAR,
        TODAY,
        YESTERDAY,
        ALL_DATES,
    } from '../../generic/date-range-picker/date-range-picker.vue';
    import ActionsCellRenderer from './cell-renderers/action-cell-renderer.vue';
    import NameCellRenderer from './cell-renderers/name-cell-renderer.vue';
    import BulkActions from '../../generic/bulk-actions/bulk-actions.vue';
    import { toAmountDisplay } from '../../../utils/numbers';
    import ResponsiveDropdown from '../../generic/responsive-dropdown/responsive-dropdown.vue';

    export default {
        components: {
            page,
            Grid,
            DateRangePicker,
            FormInput,
            SearchMultiselect,
            ExportToExcelButton,
            MoreFiltersDropdown,
            FormSelect,
            ResponsiveButton,
            BulkActions,
            FormSearchField,
            ResponsiveDropdown,
            actionsCellRenderer: ActionsCellRenderer,
            nameCellRenderer: NameCellRenderer,
        },
        name: 'AdminDamageClaimsList',
        data() {
            return {
                busy: false,
                tabs: [
                    {
                        title: 'Lodgix Claims',
                        active: true,
                        route: '/app/admin/damage-claims/',
                    },
                ],
                currentSelectedRows: [],
                selectedActionLabel: 'Actions',
                selectedAction: null,
                dateRange: nameToRange(ALL_DATES),
                dateRequestFormat: 'yyyy-MM-dd',
                predefinedRanges: [
                    LAST_MONTH,
                    LAST_WEEK,
                    LAST_YEAR,
                    THIS_MONTH,
                    THIS_WEEK,
                    THIS_YEAR,
                    TODAY,
                    YESTERDAY,
                    ALL_DATES,
                ],
                subscribersList: [],
                statusList: [],
                selectedStatus: 'open',
                selectedSubscriber: '',
                selectedUpdateStatus: '',
                paymentId: '',
                subscriberSearch: '',
                offset: 0,
                limit: 20,
                sort: 'status_order',
                desc: true,
                changeStatusLabel: 'Choose Options',
                selectedStatusLabel: 'Open Claims',
            };
        },
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat', 'userCurrencySymbol']),
            columnDefs() {
                return [
                    {
                        field: 'number',
                        headerName: 'Claim ID',
                        checkboxSelection: true,
                        cellRenderer: 'nameCellRenderer',
                        minWidth: 180,
                    },
                    {
                        field: 'amount',
                        headerName: 'Amount',
                        type: 'amountColumn',
                        minWidth: 100,
                    },
                    {
                        field: 'payment_id',
                        headerName: 'Pmt ID',
                        cellRenderer: ({ data }) => (data && data.payment_id) || 'N/A',
                    },
                    {
                        field: 'payment_date',
                        headerName: 'Pmt Date',
                        type: 'dateColumn',
                        minWidth: 130,
                        maxWidth: 140,
                        cellRendererParams: {
                            noValue: 'N/A',
                        },
                    },
                    {
                        field: 'subscriber_company_name',
                        headerName: 'Subscriber',
                        cellRenderer: ({ data }) =>
                            data &&
                            `<span>${data.subscriber_company_name}</span><br/><small class="text-small">Last Update: ${
                                data.subscriber_update_date
                                    ? format(parseISO(data.subscriber_update_date), this.userUnicodeDateFormat)
                                    : 'N/A'
                            }</small>`,
                        autoHeight: true,
                        minWidth: 200,
                        autoWidthExclude: true,
                    },
                    {
                        field: 'status_name',
                        headerName: 'Status',
                        minWidth: 200,
                        autoWidthExclude: true,
                    },
                    {
                        field: 'policy_product_type',
                        headerName: 'Policy',
                        sortable: false,
                        minWidth: 160,
                    },
                    {
                        field: 'policy_name',
                        headerName: 'Insured',
                        minWidth: 180,
                        autoWidthExclude: true,
                    },
                    {
                        field: 'age_in_days',
                        headerName: 'Age',
                        minWidth: 100,
                        maxWidth: 100,
                    },
                    {
                        field: 'submitted_date',
                        headerName: 'Opened',
                        type: 'dateColumn',
                        minWidth: 130,
                        maxWidth: 140,
                    },
                    {
                        field: '',
                        headerName: '',
                        cellRenderer: 'actionsCellRenderer',
                        cellRendererParams: {
                            updateNotes: async (note, id) => {
                                try {
                                    await API.patch(`${ADMIN_DAMAGE_CLAIMS_LIST_ENDPOINT()}${id}/`, {
                                        id,
                                        admin_notes: note,
                                    });
                                    this.$bvModal.hide('notes-modal-' + id);
                                    this.successToast({
                                        text: 'Note was updated successfully.',
                                    });
                                } catch (e) {
                                    this.errorToast({
                                        text: 'Could not update note.',
                                    });
                                }
                            },
                        },
                        minWidth: 200,
                        autoWidthExclude: true,
                    },
                ];
            },
            statusId() {
                return this.selectedStatus === 'all' || this.selectedStatus === 'open'
                    ? 0
                    : Number(this.selectedStatus);
            },
            hasSelectedRows() {
                return this.currentSelectedRows.length > 0;
            },
            bulkActionDefs() {
                return [
                    {
                        label: 'Change Pmt Id',
                        group: 'Pmt Id',
                        onClick: this.selectChangePmtId,
                    },
                    {
                        label: 'Change status',
                        group: 'Claim Status',
                        onClick: this.selectChangeStatus,
                    },
                ];
            },
            excelHref() {
                return `${ADMIN_DAMAGE_CLAIMS_LIST_ENDPOINT()}excel/?offset=${this.offset}&limit=${this.limit}&sort=${
                    this.sort
                }&desc=${this.desc}&search=${this.search}&from_date=${this.startDateRequest}&to_date=${
                    this.endDateRequest
                }&${this.isClosed !== undefined ? 'closed=' + this.isClosed : ''}&subscriber_id=${
                    this.selectedSubscriber
                }&status_id=${this.statusId}`;
            },
            endDateRequest() {
                return format(this.dateRange.to, this.dateRequestFormat);
            },
            startDateRequest() {
                return format(this.dateRange.from, this.dateRequestFormat);
            },
            fetchCallback() {
                return ({ offset, limit, sort, desc, search }) => {
                    this.offset = offset;
                    this.limit = limit;
                    this.sort = sort;
                    this.desc = desc;
                    this.search = search;
                    const params = {
                        offset,
                        limit,
                        sort,
                        desc,
                        search,
                        from_date: this.startDateRequest,
                        to_date: this.endDateRequest,
                        status_id: this.statusId,
                        subscriber_id: this.selectedSubscriber,
                    };
                    if (this.selectedStatus === 'all' || this.selectedStatus === 'open') {
                        params.closed = this.isClosed;
                    }
                    return API.get(`${ADMIN_DAMAGE_CLAIMS_LIST_ENDPOINT()}grid/`, {
                        params,
                    });
                };
            },
            isClosed() {
                if (this.selectedStatus === 'all') {
                    return true;
                }
                if (this.selectedStatus === 'open') {
                    return false;
                }
                return undefined;
            },
            isPending() {
                return false;
            },
            isChangeStatusSelected() {
                return this.selectedAction && this.selectedAction === 'change_status';
            },
            isChangePmtIdSelected() {
                return this.selectedAction && this.selectedAction === 'change_pmt_id';
            },
            changeStatusOptions() {
                return this.statusList.map((item) => ({
                    label: item.text,
                    click: () => {
                        this.changeStatusLabel = item.text;
                        this.selectedUpdateStatus = item.value;
                    },
                }));
            },
            statusOptions() {
                const defaultStatuses = [
                    {
                        value: 'all',
                        text: 'All Claims',
                    },
                    {
                        value: 'open',
                        text: 'Open Claims',
                    },
                ];
                return [...defaultStatuses, ...this.statusList].map((item) => ({
                    label: item.text,
                    click: () => {
                        this.selectedStatusLabel = item.text;
                        this.selectedStatus = item.value;
                        this.refresh();
                    },
                }));
            },
            paymentAmount() {
                const amount = this.currentSelectedRows.reduce((prev, curr) => prev + curr.amount, 0);
                return toAmountDisplay({
                    amount,
                    currency: this.userCurrencySymbol,
                });
            },
        },
        mounted() {
            this.fetchStatuses();
        },
        methods: {
            async applyChanges() {
                try {
                    const ids = this.currentSelectedRows.map((item) => item.id);

                    const body = {
                        ids,
                        status: null,
                        payment_id: Number(this.paymentId),
                    };
                    if (this.isChangeStatusSelected) {
                        body.status = this.selectedUpdateStatus;
                    }
                    await API.put(`${ADMIN_DAMAGE_CLAIMS_LIST_ENDPOINT()}bulk-update/`, body);
                    this.refresh();
                    this.successToast({ text: 'Records were updated' });
                    this.payment_id = '';
                    this.currentSelectedRows = [];
                    this.$refs.grid.gridOptions.api.deselectAll();
                } catch (e) {
                    this.errorToast({ text: 'Could not update records' });
                }
            },
            selectChangePmtId() {
                this.selectedActionLabel = 'Change Pmt Id';
                this.selectedAction = 'change_pmt_id';
            },
            selectChangeStatus() {
                this.selectedActionLabel = 'Change Status';
                this.selectedAction = 'change_status';
            },
            async fetchStatuses() {
                try {
                    const { data } = await API.get(`${ADMIN_DAMAGE_CLAIMS_LIST_ENDPOINT()}statuses/?is_sdw=true`);
                    this.statusList = [
                        ...data.map((item) => ({
                            value: item.id,
                            text: item.name,
                        })),
                    ];
                    this.selectedUpdateStatus = this.statusList.length && this.statusList[0].value;
                } catch (e) {
                    this.errorToast({ text: 'Could not load statuses' });
                }
            },
            refresh() {
                this.$refs.grid.refreshDataSource();
            },
            handleSelectionChange() {
                const gridApi = this.$refs.grid.gridOptions.api;
                this.currentSelectedRows = gridApi.getSelectedRows();
            },
            onDaterangeChange(value) {
                this.dateRange = { ...value };
                this.refresh();
            },
            selectSubscriber(text, value) {
                this.subscriberSearch = text;
                this.selectedSubscriber = value;
                this.subscribersList = [];
                this.refresh();
            },
            searchSubscribers: debounce(function(value) {
                this.subscriberSearch = value;
                this.selectedSubscriber = '';
                if (value === '') {
                    this.refresh();
                } else {
                    this.fetchSubscribers();
                }
            }, 500),
            async fetchSubscribers() {
                try {
                    this.busy = true;
                    const { data } = await API.get(`${ADMIN_DAMAGE_CLAIMS_LIST_ENDPOINT()}subscribers/`, {
                        params: {
                            search: this.subscriberSearch,
                        },
                    });
                    this.subscribersList = data.map((user) => ({
                        text: `${user.id} - ${user.email}`,
                        value: user.id,
                    }));
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch subscribers' });
                } finally {
                    this.busy = false;
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
    .subscribers-list-wrapper {
        top: 40px;
    }
    .subscribers-wrapper {
        min-width: 300px;
    }
    .amount-wrapper {
        font-size: 12px;
    }

    .damage-claim-dropdown {
        :deep(.dropdown-toggle) {
            width: 100%;
        }
    }
</style>
